import { useState } from 'react'

import { useAuth } from '@/features/auth/hooks/useAuth'
import SalespersonLeadsModal from '@/modules/admin/components/dashboard/SalespersonLeadsModal'
import { Card } from '@/shared/components/ui/card'

export default function TargetAchievementCard({
  target = 0,
  achievement = 0,
  achievementRevenue = 0,
  isTeam = false,
  period,
  customRange,
}) {
  const { user } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const targetVal = Number(target) || 0
  const achievementVal = Number(achievement) || 0
  const revenueVal = Number(achievementRevenue) || 0
  const percentage =
    targetVal > 0 ? Math.round((revenueVal / targetVal) * 100) : 0
  const remainingRevenue = targetVal - revenueVal

  // Use achievementRevenue if provided, otherwise fallback to achievementVal
  // Format as Indian Rupee
  const displayRevenue = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(revenueVal)

  const displayTarget = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(targetVal)

  const displayRemaining =
    remainingRevenue < 0
      ? `₹${Math.abs(remainingRevenue).toLocaleString('en-IN')}`
      : `₹${remainingRevenue.toLocaleString('en-IN')}`

  return (
    <>
      <Card
        onClick={() => setIsModalOpen(true)}
        className="h-[260px] cursor-pointer overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.04)] transition hover:border-slate-300 hover:shadow-md sm:rounded-[20px]"
      >
        <div className="flex h-full flex-col p-4">
          {/* Header without icon */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              {isTeam ? 'Team Target' : 'Target & Achievement'}
            </h3>
            <span className="text-[10px] font-semibold text-emerald-600 hover:underline">
              View Breakdown &rarr;
            </span>
          </div>

          {/* Big Numbers Comparison */}
          <div className="mt-2.5 grid grid-cols-2 gap-2 rounded-xl border border-black/[0.04] bg-slate-50/80 p-2.5">
            <div>
              <p className="text-[10px] font-medium text-slate-500">
                Achieved (Revenue)
              </p>
              <div className="flex flex-col mt-0.5">
                <p
                  className="text-sm sm:text-base lg:text-lg font-extrabold leading-tight tracking-tighter text-emerald-600"
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {displayRevenue}
                </p>
                <p className="text-[11px] font-semibold text-emerald-700/80 mt-1">
                  {achievementVal} {achievementVal === 1 ? 'unit' : 'units'}
                </p>
              </div>
            </div>
            <div className="border-l border-slate-200 pl-2.5">
              <p className="text-[10px] font-medium text-slate-500">
                {isTeam ? 'Team Target' : 'Assigned Target'}
              </p>
              <div className="flex flex-col mt-0.5">
                <p
                  className="text-sm sm:text-base lg:text-lg font-extrabold leading-tight tracking-tighter text-slate-900"
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {displayTarget}
                </p>
                <p className="text-[11px] font-semibold text-slate-500 mt-1">
                  personal target
                </p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-2.5 space-y-1">
            <div className="flex items-center justify-between text-[10px]">
              <span className="font-medium text-slate-500">Progress</span>
              <span className="font-bold text-slate-800">{percentage}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                style={{ width: `${Math.min(100, percentage)}%` }}
              />
            </div>
          </div>

          {/* Breakdown details */}
          <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-2 text-[11px]">
            <span className="text-slate-500">Remaining to Target</span>
            <span className="font-bold text-slate-900">{displayRemaining}</span>
          </div>
        </div>
      </Card>

      <SalespersonLeadsModal
        person={{
          salesId: user?.id || user?._id,
          name: user?.name,
          target: targetVal,
          revenue: revenueVal,
          orders: achievementVal,
          won: achievementVal,
        }}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        period={period}
        customRange={customRange}
        leadsUrl="/sales/leads"
      />
    </>
  )
}
